/**
 * StatCard Component
 * 
 * Individual statistics card displaying a metric with icon.
 * Used within StatsGrid for consistent dashboard-style statistics.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface StatCardProps {
  /** Label/title for the statistic */
  label: string;
  /** Value to display (number or formatted string) */
  value: number | string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Background color class for icon container (e.g., 'bg-blue-100') */
  iconBgColor: string;
  /** Icon color class (e.g., 'text-blue-600') */
  iconColor: string;
  /** Optional value color class (e.g., 'text-green-600') */
  valueColor?: string;
  /** Optional subtitle/additional info */
  subtitle?: string;
}

export function StatCard({
  label,
  value,
  icon: Icon,
  iconBgColor,
  iconColor,
  valueColor = 'text-gray-900',
  subtitle,
}: StatCardProps) {
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className={`text-2xl font-bold ${valueColor}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 ${iconBgColor} rounded-lg`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}
